import React, { useEffect, useState, useRef, MutableRefObject } from 'react';
import { useSelector } from 'react-redux';

// types
import type { WCmsPhotoView } from '@zola/svc-web-api-ts-client';
import type { GenericEntityType } from 'actions/types/website/EntityActionTypes';
import type {
  PhotosMapStateProps,
  PhotosMapDispatchProps,
} from 'components/manage/website/entities/Photos';
import { ZolaFileResponseType } from 'actions/types/UploadcareActions';

// util
import { buildCreatePhotosPayload } from 'components/common/MultiPhotoUpload/utils';
import { filterEntitiesById, filterEntitiesOrderedIds } from 'util/filterEntities';
import { getImageUrl } from 'cards/util/imageHelper';
import experimentFlags, { GALLERY_MULTI_PHOTO } from 'util/experimentFlags';

// actions
import { entityNames } from 'actions/PublicWebsiteActions';

// selectors
import { getUserContext } from 'selectors/user/userSelectors';

// components
import AddPhotoDetailsModal from 'components/manage/website/entities/Photos/components/AddPhotoDetailsModal';
import MultiPhotoUpload from 'components/common/MultiPhotoUpload';
import WebsiteVideoUpload from 'components/manage/website/WebsiteVideoUpload';
import WebsiteVideoUploadModal from 'components/manage/website/WebsiteVideoUploadModal';
import { VideoCaption, VideoDetails } from '../EditPageCommon/entityDetails/VideoDetails';
import EditPageAddEntity from '../EditPageCommon/EditPageAddEntity';
import EditPageForm from '../EditPageCommon/EditPageFormContainer/EditPageFormContainer';
import EditPageHeader from '../EditPageCommon/EditPageHeader';
import FileInputButton from '../../../../common/form/FileInputButton';

// styles
import { PhotoEntityPreview } from '../EditPageCommon/EditPageSortableList/EditPageSortableList.styles';
import './Photos.less';

const PhotoDetails = ({ image_id }: GenericEntityType = {}): JSX.Element => (
  <>{image_id && <PhotoEntityPreview image={getImageUrl(image_id)} />}</>
);

type PhotosProps = PhotosMapDispatchProps & PhotosMapStateProps;

const Photos = ({
  closeModal,
  entities,
  getPhotoEntities,
  onEdit,
  onPageToggle,
  onPhotoSelect,
  openCropModal,
  openSaveModal,
  orderedIds,
  page,
  photoFilePreview,
  submitMultiPhotos,
  themeKey,
  unloadEntities,
}: PhotosProps): JSX.Element => {
  const [editingEntity, setEditingEntity] = useState<GenericEntityType | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [triggeringRef, setTriggeringRef] = useState<MutableRefObject<null> | null>(null);
  const photoOrderedIds = filterEntitiesOrderedIds(orderedIds, entities, entityNames.photos);
  const photos = filterEntitiesById(entities, entityNames.photos);
  const videoOrderedIds = filterEntitiesOrderedIds(orderedIds, entities, entityNames.videos);
  const videos = filterEntitiesById(entities, entityNames.videos);
  const userContext = useSelector(getUserContext);
  const galleryMultiPhoto = experimentFlags.getFlag(GALLERY_MULTI_PHOTO, userContext);
  const [isPhotoDetailsModalOpen, setIsPhotoDetailsModalOpen] = useState(false);
  const photoDetailsRef = useRef<HTMLButtonElement>(null);
  const [multiPhotosQueue, setMultiPhotosQueue] = useState<Record<number, WCmsPhotoView>>([]);
  const [multiPhotosQueueKey, setmultiPhotosQueueKey] = useState<string>('multiPhotosQueueKey');

  const photoCount = Object.keys(photos).length;
  const pageType = page && page.type;
  const { env } = window.zola;
  let photoLimit: number;
  if (env === 'development') {
    photoLimit = 25;
  } else if (env === 'staging') {
    photoLimit = 25;
  } else {
    photoLimit = 100;
  }
  const nearPhotoLimit = photoCount >= photoLimit - 5 && photoCount < photoLimit;
  const atPhotoLimit = photoCount >= photoLimit;
  const hasNoPhotos = photoCount === 0;

  useEffect(() => {
    getPhotoEntities();
    return () => unloadEntities();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (photoFilePreview?.s3_key) {
      openCropModal(photoFilePreview);
    } else if (photoFilePreview?.uuid) {
      closeModal();
      openSaveModal(photoFilePreview);
    }
  }, [photoFilePreview, openCropModal, openSaveModal, closeModal]);

  const handleToggleModal = () => {
    if (modalOpen) {
      if (editingEntity) setEditingEntity(undefined);
      setTriggeringRef(null);
    }
    setModalOpen(!modalOpen);
  };

  const openAddVideoEntityModal = (ref: MutableRefObject<null>) => {
    handleToggleModal();
    setTriggeringRef(ref);
  };

  const openEditVideoEntityModal = (entity: GenericEntityType, ref: MutableRefObject<null>) => {
    handleToggleModal();
    setTriggeringRef(ref);
    setEditingEntity(entity);
  };

  const handleTogglePhotoDetailsModal = (): void => {
    setIsPhotoDetailsModalOpen(!isPhotoDetailsModalOpen);
  };

  const onUploadCompleted = async (zolaFiles: ZolaFileResponseType[]) => {
    const payload = buildCreatePhotosPayload({
      zolaFiles,
    });
    const newPhotos = await submitMultiPhotos(payload, photoLimit);
    if (newPhotos && newPhotos.length > 0) {
      const photosQueue: Record<number, WCmsPhotoView> = {};
      newPhotos.forEach(photo => {
        if (photo.id) {
          photosQueue[photo.id] = photo;
        }
      });
      setMultiPhotosQueue(photosQueue);
      setIsPhotoDetailsModalOpen(true);
    }
  };

  const removePhotoFromMultiQueue = (id: number) => {
    const updatedMultiPhotosQueue = { ...multiPhotosQueue };
    delete updatedMultiPhotosQueue[id];
    setMultiPhotosQueue(updatedMultiPhotosQueue);
  };

  const updatePhotoInMultiQueue = (id: number, newPhoto: ZolaFileResponseType) => {
    const updatedMultiPhotosQueue = { ...multiPhotosQueue };
    updatedMultiPhotosQueue[id].image_id = newPhoto.uuid;
    updatedMultiPhotosQueue[id].image_url = newPhoto.url;
    setMultiPhotosQueue(updatedMultiPhotosQueue);
  };

  const updatePhotoCaptionInMultiQueue = (id: number, newCaption: string) => {
    const updatedMultiPhotosQueue = { ...multiPhotosQueue };
    updatedMultiPhotosQueue[id].caption = newCaption;
    setMultiPhotosQueue(updatedMultiPhotosQueue);
  };

  const photoBtnTxt = (): string => {
    if (photoFilePreview?.busy) {
      return 'Uploading...';
    }
    if (galleryMultiPhoto) {
      return 'Add More Photos';
    }
    return 'Add A Photo';
  };
  const button = (
    <div>
      <div className="upload-photo">
        <FileInputButton
          inputId="new_photo"
          onSelect={onPhotoSelect}
          types="image/*"
          btnClass="margin-bottom uploadPhoto__button"
          btnText={photoBtnTxt()}
          busyOverride
          busy={photoFilePreview?.busy}
          disabledOverride={atPhotoLimit}
        />
        {nearPhotoLimit && (
          <div className="photo-limit">
            <p>
              {photoCount}/{photoLimit} Photos Added
            </p>
          </div>
        )}
        {atPhotoLimit && (
          <div className="photo-limit">
            <p>Limit Reached: {photoLimit} Photos Added</p>
          </div>
        )}
      </div>
      {!galleryMultiPhoto && (
        <>
          &nbsp;
          <small>(max file size 5 MB)</small>
        </>
      )}
    </div>
  );

  if (galleryMultiPhoto) {
    return (
      <React.Fragment>
        {themeKey && <EditPageHeader page={page} themeKey={themeKey} onPageToggle={onPageToggle} />}
        {/* @ts-ignore */}
        <EditPageForm page={page} galleryMultiPhoto={galleryMultiPhoto} />
        {videoOrderedIds.length ? (
          <EditPageAddEntity
            entityName="Video"
            entitiesListTitle="Videos"
            entityApiName={entityNames.videos}
            pageType={pageType}
            orderedIds={videoOrderedIds}
            entities={videos}
            entityDetailProps={{
              onEdit: openEditVideoEntityModal,
              entityDetail: VideoDetails,
              entitySecondaryDetail: VideoCaption,
              entityEditRemoveName: 'video',
            }}
            onAdd={openAddVideoEntityModal}
          />
        ) : (
          <WebsiteVideoUpload
            label="Favorite clips add even more personality to your gallery."
            page="gallery"
          />
        )}
        {hasNoPhotos ? (
          <MultiPhotoUpload
            analyticsLocation="WEBSITE"
            analyticsProduct="WEBSITE"
            onUploadComplete={zolaFiles => {
              onUploadCompleted(zolaFiles);
            }}
            isDisabled={atPhotoLimit}
          />
        ) : (
          <EditPageAddEntity
            key={multiPhotosQueueKey}
            entityName="Photo"
            entitiesListTitle="Photos"
            entityApiName={entityNames.photos}
            entityDetailProps={{
              onEdit,
              entityDetail: PhotoDetails,
              entityEditRemoveName: 'photo',
            }}
            onAddButton={
              <MultiPhotoUpload
                analyticsLocation="WEBSITE"
                analyticsProduct="WEBSITE"
                btnView
                onUploadComplete={zolaFiles => {
                  onUploadCompleted(zolaFiles);
                }}
                isDisabled={atPhotoLimit}
              />
            }
            pageType={pageType}
            orderedIds={photoOrderedIds}
            entities={photos}
          />
        )}
        {atPhotoLimit && (
          <div className="photo-limit">
            <p>Limit Reached: {photoLimit} Photos Added</p>
          </div>
        )}
        {modalOpen && (
          <WebsiteVideoUploadModal
            onClose={handleToggleModal}
            triggerRef={triggeringRef}
            entity={editingEntity}
            page="gallery"
          />
        )}
        {isPhotoDetailsModalOpen && (
          <AddPhotoDetailsModal
            handleModalClose={handleTogglePhotoDetailsModal}
            modalTriggerRef={photoDetailsRef}
            photos={multiPhotosQueue}
            removePhotoFromMultiQueue={removePhotoFromMultiQueue}
            updatePhotoInMultiQueue={updatePhotoInMultiQueue}
            updatePhotoCaptionInMultiQueue={updatePhotoCaptionInMultiQueue}
            updateMultiPhotosQueueKey={setmultiPhotosQueueKey}
            photoOrderedIdsFullList={photoOrderedIds}
          />
        )}
      </React.Fragment>
    );
  }

  return (
    <div>
      {themeKey && <EditPageHeader page={page} themeKey={themeKey} onPageToggle={onPageToggle} />}
      <>
        {/* @ts-ignore */}
        <EditPageForm page={page} />
        {videoOrderedIds.length ? (
          <EditPageAddEntity
            entityName="Video"
            entitiesListTitle="Videos"
            entityApiName={entityNames.videos}
            pageType={pageType}
            orderedIds={videoOrderedIds}
            entities={videos}
            entityDetailProps={{
              onEdit: openEditVideoEntityModal,
              entityDetail: VideoDetails,
              entitySecondaryDetail: VideoCaption,
              entityEditRemoveName: 'video',
            }}
            onAdd={openAddVideoEntityModal}
          />
        ) : (
          <WebsiteVideoUpload
            label="Favorite clips add even more personality to your gallery."
            page="gallery"
          />
        )}
        <EditPageAddEntity
          entityName="Photo"
          entitiesListTitle="Photos"
          entityApiName={entityNames.photos}
          entityDetailProps={{
            onEdit,
            entityDetail: PhotoDetails,
            entityEditRemoveName: 'photo',
          }}
          onAddButton={button}
          pageType={pageType}
          orderedIds={photoOrderedIds}
          entities={photos}
        />
      </>
      {modalOpen && (
        <WebsiteVideoUploadModal
          onClose={handleToggleModal}
          triggerRef={triggeringRef}
          entity={editingEntity}
          page="gallery"
        />
      )}
    </div>
  );
};

export default Photos;
